/**
 * Scene Details Panel Component
 * Sprint 19+: Enhanced Scene Cards
 * 
 * Expandable panel showing:
 * - Script preview (first 4 lines with expand)
 * - Layout name and configuration
 * - Background details
 * - Recording statistics (takes, duration)
 * - Smooth expand/collapse animation
 */

import React from 'react';
import { Collapse, Space, Divider } from 'antd';
import { 
  FileText, 
  Layout, 
  Palette, 
  Film, 
  Clock, 
  ChevronDown,
  Tag,
  FileEdit
} from 'lucide-react';
import { Scene } from '../../../../../types/scene';
import { SceneTagSelector } from './SceneTagSelector';
import { SceneNotesField } from './SceneNotesField';
import { SceneSettingsBadges } from './SceneSettingsBadges';
import { SceneDurationProgress } from './SceneDurationProgress';
import './SceneDetailsPanel.scss';

const { Panel } = Collapse;

interface SceneDetailsPanelProps {
  scene: Scene;
  isExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  onTagsChange?: (tags: any[]) => void;
  onNotesChange?: (notes: string) => void;
  readOnly?: boolean;
}

export const SceneDetailsPanel: React.FC<SceneDetailsPanelProps> = ({
  scene,
  isExpanded = false,
  onExpandChange,
  onTagsChange,
  onNotesChange,
  readOnly = false
}) => {
  // Format script preview (first 4 lines or 200 chars)
  const getScriptPreview = () => {
    if (!scene.script) return 'No script configured';
    
    const lines = scene.script.split('\n').slice(0, 4);
    const preview = lines.join('\n');
    
    if (preview.length > 200) {
      return preview.substring(0, 200) + '...';
    }
    
    return preview;
  };

  // Get background display info
  const getBackgroundInfo = () => {
    if (!scene.background) {
      return { text: 'No background', visual: null };
    }

    switch (scene.background.type) {
      case 'solid':
        return {
          text: `Solid Color: ${scene.background.colors?.[0] || 'N/A'}`,
          visual: (
            <div 
              className="background-preview solid"
              style={{ backgroundColor: scene.background.colors?.[0] }}
            />
          )
        };
      case 'color': {
        const colorVal = scene.background.value || '#000000';
        return {
          text: `Solid Color: ${colorVal}`,
          visual: (
            <div 
              className="background-preview solid"
              style={{ backgroundColor: colorVal }}
            />
          )
        };
      }
      case 'gradient':
        return {
          text: `Gradient (${scene.background.colors?.length || 0} colors)`,
          visual: (
            <div 
              className="background-preview gradient"
              style={{ 
                background: `linear-gradient(135deg, ${scene.background.colors?.join(', ')})` 
              }}
            />
          )
        };
      case 'image':
        return {
          text: 'Background Image',
          visual: (
            <div 
              className="background-preview image"
              style={{ 
                backgroundImage: `url(${scene.background.imageUrl})` 
              }}
            />
          )
        };
      default:
        return { text: 'Unknown', visual: null };
    }
  };

  const backgroundInfo = getBackgroundInfo();
  const hasRecordings = scene.recordingHistory && scene.recordingHistory.length > 0;
  const takesCount = scene.recordingHistory?.length || 0;
  const keptTakes = scene.recordingHistory?.filter(t => t.isKept).length || 0;

  const formatDuration = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="scene-details-panel">
      <Collapse
        activeKey={isExpanded ? ['details'] : []}
        onChange={(keys) => onExpandChange?.(keys.includes('details'))}
        expandIcon={({ isActive }) => <ChevronDown className={isActive ? 'rotated' : ''} size={16} />}
        ghost
        className="details-collapse"
      >
        <Panel 
          header={
            <div className="panel-header">
              <span className="header-text">Scene Details</span>
              <span className="header-hint">
                {isExpanded ? 'Click to collapse' : 'Click to expand'}
              </span>
            </div>
          } 
          key="details"
          className="details-panel"
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Duration Progress */}
            {scene.suggestedDuration && (
              <>
                <div className="detail-section">
                  <div className="section-header">
                    <Clock size={14} />
                    <span className="section-title">Duration</span>
                  </div>
                  <div className="section-content">
                    <SceneDurationProgress
                      actualDuration={scene.duration}
                      suggestedDuration={scene.suggestedDuration}
                      compact={false}
                    />
                  </div>
                </div>
                <Divider style={{ margin: 0 }} />
              </>
            )}

            {/* Settings Badges */}
            <div className="detail-section">
              <div className="section-header">
                <Layout size={14} />
                <span className="section-title">Settings</span>
              </div>
              <div className="section-content">
                <SceneSettingsBadges scene={scene} />
              </div>
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Tags Section */}
            {((scene.tags && scene.tags.length > 0) || !readOnly) && (
              <>
                <div className="detail-section">
                  <div className="section-header">
                    <Tag size={14} />
                    <span className="section-title">Tags</span>
                  </div>
                  <div className="section-content">
                    <SceneTagSelector
                      selectedTags={scene.tags || []}
                      onChange={(tags) => onTagsChange?.(tags)}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                <Divider style={{ margin: 0 }} />
              </>
            )}

            {/* Notes Section */}
            {(scene.notes || !readOnly) && (
              <>
                <div className="detail-section">
                  <div className="section-header">
                    <FileEdit size={14} />
                    <span className="section-title">Production Notes</span>
                  </div>
                  <div className="section-content">
                    <SceneNotesField
                      value={scene.notes}
                      onChange={(notes) => onNotesChange?.(notes)}
                      disabled={readOnly}
                    />
                  </div>
                </div>
                <Divider style={{ margin: 0 }} />
              </>
            )}

            {/* Script Section */}
            <div className="detail-section">
              <div className="section-header">
                <FileText size={14} />
                <span className="section-title">Script</span>
              </div>
              <div className="section-content script-preview">
                {getScriptPreview()}
              </div>
              {scene.script && scene.script.length > 200 && (
                <div className="script-meta">
                  {scene.script.split('\n').length} lines • {scene.script.length} characters
                </div>
              )}
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Layout Section */}
            <div className="detail-section">
              <div className="section-header">
                <Layout size={14} />
                <span className="section-title">Layout</span>
              </div>
              <div className="section-content">
                <div className="layout-info">
                  <span className="info-label">Preset:</span>
                  <span className="info-value">{scene.layoutPreset || 'Not configured'}</span>
                </div>
                <div className="layout-info">
                  <span className="info-label">Elements:</span>
                  <span className="info-value">{scene.layout?.length || 0} items</span>
                </div>
              </div>
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Background Section */}
            <div className="detail-section">
              <div className="section-header">
                <Palette size={14} />
                <span className="section-title">Background</span>
              </div>
              <div className="section-content">
                <div className="background-info">
                  {backgroundInfo.visual}
                  <span className="info-text">{backgroundInfo.text}</span>
                </div>
              </div>
            </div>

            {/* Recording Stats (if any) */}
            {hasRecordings && (
              <>
                <Divider style={{ margin: 0 }} />
                <div className="detail-section">
                  <div className="section-header">
                    <Film size={14} />
                    <span className="section-title">Recording History</span>
                  </div>
                  <div className="section-content">
                    <div className="recording-stats">
                      <div className="stat-item">
                        <span className="stat-label">Total Takes:</span>
                        <span className="stat-value">{takesCount}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Kept:</span>
                        <span className="stat-value success">{keptTakes}</span>
                      </div>
                      {scene.duration > 0 && (
                        <div className="stat-item">
                          <span className="stat-label">Duration:</span>
                          <span className="stat-value">{formatDuration(scene.duration)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </Space>
        </Panel>
      </Collapse>
    </div>
  );
};
